import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { UserMessage, UserNotification, UserNotificationCommand } from './notification-bar.model';
import * as _ from 'lodash';
import { Subscription, Observable } from 'rxjs';

export type CommonUserMessageKey = keyof NotificationBarComponent['_commonUserMessages'];

@Component({
  selector: 'ssg-notification-bar',
  templateUrl: './notification-bar.component.html',
  styleUrls: ['./notification-bar.component.less']
})
export class NotificationBarComponent implements OnInit, OnDestroy {

  private notificationSubscription: Subscription;
  private _commonUserMessages = {
    conflict: new UserMessage({ message: 'COMMON.CONFLICT', severity: 'warning' }),
    badRequest: new UserMessage({ message: 'COMMON.BAD_REQUEST', severity: 'important' }),
    internalServerError: new UserMessage({ message: 'COMMON.SERVER_ERROR', severity: 'important' }),
  };

  @Input() userMessages: { [key: string]: UserMessage } = {};
  @Input() showMultiple = true;
  @Input() pageKey: string;
  @Input() notification: Observable<UserNotificationCommand>;

  public notifications: UserNotification[] = [];

  constructor() { }

  ngOnInit() {
    this._restoreValuesFromCache(sessionStorage.getItem(this.pageKey));
    this._combineUserMessages();
    this.notificationSubscription = this.notification.subscribe((command) =>
      this._handleNotificationCommand(command));
  }

  ngOnDestroy() {
      this.notificationSubscription.unsubscribe();
      this._storeNotifications();
  }

  dismiss(index: number) {
    if (this.userMessages[this.notifications[index].key].persistToStorage) {
      this.notifications[index].dismissed = true;
    } else {
      this.notifications.splice(index, 1);
    }
  }

  private _handleNotificationCommand(command: UserNotificationCommand) {
    if (command.action === 'add') {
        this._addNotification(command.userNotification);
    } else if (command.action === 'clearKey') {
      this._clearNotificationKey(command.userNotification);
    } else if (command.action === 'clearType') {
      this._clearNotificationType(command.userNotification);
    }
  }

  private _addNotification(notif: UserNotification) {
    if (this.userMessages[notif.key].distinct && _.some(this.notifications, {key: notif.key})) {
        return;
    }
    this.notifications.push(notif);
  }

  private _clearNotificationKey(notif: UserNotification) {
    if (notif) {
      this.notifications = _.filter(this.notifications, (n) => notif.key !== n.key);
    }
  }

  private _clearNotificationType(notif: UserNotification) {
    this.notifications = _.filter(this.notifications, (n) => this.userMessages[notif.key].type !== this.userMessages[n.key].type);
  }

  private _storeNotifications() {
    const notificationsToPersist = [];
    this.notifications.forEach(notif => {
      if (this.userMessages[notif.key].persistToStorage) {
          notificationsToPersist.push(notif);
      }
    });
    sessionStorage.setItem(this.pageKey, JSON.stringify({ notifications: notificationsToPersist}));
  }

  private _combineUserMessages() {
    const additionalMessages = _.mapValues(this.userMessages,
      function(um) {
        return new UserMessage(um);
      });
      this.userMessages = _.merge(this._commonUserMessages, additionalMessages);
  }

  private _restoreValuesFromCache(storedValues: string) {
    if (storedValues) {
      this._initializeNotifications(JSON.parse(storedValues));
    }
  }

  private _initializeNotifications(storedValues: any) {
    if (storedValues.notifications) {
      this.notifications = storedValues.notifications;
    }
  }
}
